import { Fragment, useEffect, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { SpotImage } from "@/common/model/spotimages/SpotImage";

interface ImageLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  image: SpotImage | null;
  images: SpotImage[];  // All images for navigation
  isCover: boolean;
  onSetCover?: (filename: string) => void;
  onAddToSelection?: (filename: string) => void;
  onRemoveFromSelection?: (filename: string) => void;
  onDelete?: (filename: string) => void;
  onNavigate: (image: SpotImage) => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  isOpen,
  onClose,
  image,
  images,
  isCover,
  onSetCover,
  onAddToSelection,
  onRemoveFromSelection,
  onDelete,
  onNavigate,
}) => {
  const currentIndex = image ? images.findIndex((img) => img.filename === image.filename) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const handlePrev = useCallback(() => {
    if (hasPrev) {
      onNavigate(images[currentIndex - 1]);
    }
  }, [hasPrev, currentIndex, images, onNavigate]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      onNavigate(images[currentIndex + 1]);
    }
  }, [hasNext, currentIndex, images, onNavigate]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowLeft":
          handlePrev();
          break;
        case "ArrowRight":
          handleNext();
          break;
        case "Escape":
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handlePrev, handleNext, onClose]);

  if (!image) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/90 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-5xl">
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="h-8 w-8" />
                </button>

                {/* Navigation arrows */}
                {hasPrev && (
                  <button
                    onClick={handlePrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 p-2 text-white hover:text-gray-300 transition-colors"
                  >
                    <ChevronLeftIcon className="h-12 w-12" />
                  </button>
                )}
                {hasNext && (
                  <button
                    onClick={handleNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 p-2 text-white hover:text-gray-300 transition-colors"
                  >
                    <ChevronRightIcon className="h-12 w-12" />
                  </button>
                )}

                {/* Image */}
                <div className="relative rounded-lg overflow-hidden bg-gray-900">
                  <img
                    src={image.publicUrl}
                    alt={image.filename}
                    className="w-full h-auto max-h-[80vh] object-contain"
                  />

                  {/* Image info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Score badge */}
                        {image.score !== null ? (
                          <span className="px-3 py-1 bg-blue-500/80 text-white text-sm font-medium rounded-full">
                            Score: {(image.score * 100).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-500/80 text-white text-sm rounded-full">
                            No score
                          </span>
                        )}

                        {/* Status badge */}
                        {image.selected ? (
                          <span className="px-3 py-1 bg-green-500/80 text-white text-sm rounded-full">
                            Selected
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-gray-600/80 text-white text-sm rounded-full">
                            Available
                          </span>
                        )}

                        {/* Cover badge */}
                        {isCover && (
                          <span className="px-3 py-1 bg-yellow-500/80 text-white text-sm font-medium rounded-full flex items-center gap-1">
                            <StarIconSolid className="h-4 w-4" />
                            Cover
                          </span>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2">
                        {/* Set as cover (only for selected images) */}
                        {image.selected && !isCover && onSetCover && (
                          <button
                            onClick={() => onSetCover(image.filename)}
                            className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors"
                            title="Set as cover"
                          >
                            <StarIcon className="h-5 w-5" />
                          </button>
                        )}

                        {/* Add to selection (only for available images) */}
                        {!image.selected && onAddToSelection && (
                          <button
                            onClick={() => onAddToSelection(image.filename)}
                            className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                            title="Add to selection"
                          >
                            <PlusIcon className="h-5 w-5" />
                          </button>
                        )}

                        {/* Remove from selection (only for selected images) */}
                        {image.selected && onRemoveFromSelection && (
                          <button
                            onClick={() => onRemoveFromSelection(image.filename)}
                            className="p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
                            title="Remove from selection"
                          >
                            <MinusIcon className="h-5 w-5" />
                          </button>
                        )}

                        {/* Delete permanently */}
                        {onDelete && (
                          <button
                            onClick={() => onDelete(image.filename)}
                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            title="Delete permanently"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Navigation indicator */}
                    <div className="mt-2 text-center text-gray-400 text-sm">
                      {currentIndex + 1} of {images.length}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ImageLightbox;
